import { ReactElement, ReactNode, useEffect, useState } from "react";
import type { ShellPackageDates } from "../../shared/buildDates";
import { loadComparatorState, resolveAppLinks } from "./appHeaderLinks";
import { loadMatchedScreenIds } from "./screenMatch";
import "./appHeader.css";

/** Which shell is rendering the header; picks the tint and the current tab. */
export type AppHeaderApp = "bv" | "express";

interface AppHeaderProps {
  app: AppHeaderApp;
  /** UAT builds keep their dark brown (BV) / navy (Express) bars. */
  uat?: boolean;
  /** Shell status or name: "Loading...", "BV Web", "BV Express · ios", ... */
  title?: string;
  dates?: ShellPackageDates;
  warning?: string;
  /** Course + screen the other apps should open; tabs fall back to the bare app when absent. */
  exportId?: string;
  screenId?: string;
  /** Right-side cluster (icon buttons). */
  actions?: ReactNode;
}

const HomeIcon = (): ReactElement => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.5 7.6 8 2.9l5.5 4.7" />
    <path d="M4 6.9V13a.6.6 0 0 0 .6.6h6.8a.6.6 0 0 0 .6-.6V6.9" />
    <path d="M6.6 13.6V9.7h2.8v3.9" />
  </svg>
);

/**
 * The header bar shared with the comparator report and the :3030 home page (see
 * appHeader.css / appHeaderLinks.js - the markup here mirrors `renderAppHeaderTabs`).
 */
export const AppHeader = ({
  app,
  uat = false,
  title,
  dates,
  warning,
  exportId,
  screenId,
  actions,
}: AppHeaderProps): ReactElement => {
  // the Comparator tab appears only once the comparison data is known to exist (the page's
  // ?ctx=, else the course's comp-<exportId>); for a ctx over two exports it also brings the
  // screen pairs the other side's tab opens. It runs again on every screen change: on the
  // hosted shells the first try fires before the login has primed the CloudFront cookies
  // guarding the data, and a miss is not cached.
  const [comparison, setComparison] = useState<{
    reportExists: boolean;
    screenMap: Record<string, string> | null;
  }>({ reportExists: false, screenMap: null });
  useEffect(() => {
    let cancelled = false;
    void loadComparatorState(window.location.href, exportId).then((state) => {
      if (!cancelled) {
        setComparison(state);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [exportId, screenId]);
  // the screen in the other export of a ctx, or across the EPUB boundary, matched by toc names
  // (screenMatch.js)
  const [matchedScreenIds, setMatchedScreenIds] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    void loadMatchedScreenIds(window.location.href, app, exportId, screenId).then((matched) => {
      if (!cancelled) {
        setMatchedScreenIds(matched);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [app, exportId, screenId]);

  const links = resolveAppLinks({
    href: window.location.href,
    app,
    exportId,
    screenId,
    matchedScreenIds,
    reportExists: comparison.reportExists,
    screenMap: comparison.screenMap,
  });
  const variant = `app-header--${app}${uat ? "-uat" : ""}`;
  const hasDates = Boolean(dates?.shellAt || dates?.packageAt);

  return (
    <header className={`app-header ${variant}`}>
      <div className="app-header__lead">
        {title && <h1 className="app-header__title">{title}</h1>}
        {hasDates && (
          <span className="app-header__meta">
            {dates?.shellAt && (
              <span className="app-header__date" title={dates.shellTooltip}>
                {dates.shellAt.replace(" ", "")}
              </span>
            )}
            {dates?.packageAt && (
              <span className="app-header__date" title={dates.packageTooltip}>
                {" "}
                <strong>content</strong>
                {dates.packageAt.replace(" ", "")}
              </span>
            )}
          </span>
        )}
        {warning && <span className="app-header__warning">{warning}</span>}
      </div>

      <div className="app-header__nav">
        {links.home && (
          <a className="app-header__home" href={links.home} title="Home" aria-label="Home">
            <HomeIcon />
          </a>
        )}
        <div className="app-header__tabs" role="navigation" aria-label="Switch app">
          {links.tabs.map((tab) => {
            if (tab.hidden) {
              return null;
            }
            if (tab.current) {
              return (
                <span key={tab.key} className="app-header__tab is-current" aria-current="page">
                  {tab.label}
                </span>
              );
            }
            if (tab.href) {
              return (
                <a
                  key={tab.key}
                  className="app-header__tab"
                  href={tab.href}
                  title={`Open in ${tab.label}`}
                >
                  {tab.label}
                </a>
              );
            }
            return (
              <span
                key={tab.key}
                className="app-header__tab is-disabled"
                aria-disabled="true"
                title={tab.reason}
              >
                {tab.label}
              </span>
            );
          })}
        </div>
      </div>

      <div className="app-header__actions">{actions}</div>
    </header>
  );
};

export default AppHeader;
